import {
	aiActorTypeIds,
	type CreateTicketBody,
	type MembershipListResponse,
	mentionHandle,
	type TicketMention,
	type TicketStatus,
	type UpdateTicketBody,
} from "@zplanem/responses"
import type { MentionCandidate } from "../../../components/chat/use-mention-compose"
import { getDisplayName } from "../../../lib/user-display"

interface TicketSelectOption {
	value: string
	label: string
}

export function buildAssigneeOptions(
	memberships: MembershipListResponse | undefined,
	unassignedLabel: string,
): TicketSelectOption[] {
	const options: TicketSelectOption[] = [{ value: "", label: unassignedLabel }]
	if (!memberships) return options

	const seen = new Set<string>()
	for (const user of [
		memberships.owner,
		...memberships.memberships.map((membership) => membership.user),
	]) {
		if (seen.has(user.id)) continue
		seen.add(user.id)
		options.push({ value: user.id, label: getDisplayName(user) })
	}
	return options
}

export function buildTicketMentionCandidates(
	memberships: MembershipListResponse | undefined,
	viewerId: string | undefined,
): MentionCandidate[] {
	const source = memberships
		? [memberships.owner, ...memberships.memberships.map((m) => m.user)]
		: []
	const seen = new Set<string>()
	return source
		.filter((user) => {
			if (user.id === viewerId || seen.has(user.id)) return false
			seen.add(user.id)
			return true
		})
		.map((user) => {
			const displayName = getDisplayName(user)
			return {
				key: user.id,
				kind: "user" as const,
				userId: user.id,
				displayName,
				handle: mentionHandle(displayName),
				email: user.email,
			}
		})
}

export function selectedMentionCandidatesFromMentions(
	mentions: readonly TicketMention[] | null | undefined,
	people: readonly MentionCandidate[],
): MentionCandidate[] {
	const out: MentionCandidate[] = []
	for (const mention of mentions ?? []) {
		if (
			mention.type === "ai" &&
			mention.actorId === aiActorTypeIds.mareczekAi
		) {
			out.push({
				key: aiActorTypeIds.mareczekAi,
				kind: "ai",
				displayName: "mareczek.ai",
				handle: "mareczek.ai",
			})
			continue
		}
		if ("userId" in mention && mention.userId) {
			const candidate = people.find((item) => item.userId === mention.userId)
			out.push({
				key: mention.userId,
				kind: "user",
				userId: mention.userId,
				displayName: candidate?.displayName ?? mention.handle,
				handle: mention.handle,
				email: candidate?.email,
			})
		}
	}
	return out
}

type TicketMentionRequestFields = Pick<
	CreateTicketBody & UpdateTicketBody,
	"mentionUserIds" | "mentionsMareczek"
>

export function ticketMentionRequestFields(mentions: {
	userIds: string[]
	mareczek: boolean
	includeEmpty?: boolean
}): TicketMentionRequestFields {
	return {
		...(mentions.userIds.length > 0 || mentions.includeEmpty
			? { mentionUserIds: mentions.userIds }
			: {}),
		...(mentions.mareczek || mentions.includeEmpty
			? { mentionsMareczek: mentions.mareczek }
			: {}),
	}
}

export function isTicketManager(
	memberships: MembershipListResponse | undefined,
	userId: string | undefined,
): boolean {
	if (!memberships || !userId) return false
	if (memberships.owner.id === userId) return true
	return memberships.memberships.some(
		(membership) =>
			membership.user.id === userId && membership.role === "architect",
	)
}

export function getAllowedTicketStatusTargets(
	status: TicketStatus,
	_canManage: boolean,
): TicketStatus[] {
	const allStatuses: TicketStatus[] = [
		"open",
		"in_progress",
		"done",
		"cancelled",
	]
	return [status, ...allStatuses.filter((candidate) => candidate !== status)]
}

export function canMoveTicketToStatus(
	ticket: { status: TicketStatus; assignee?: { id: string } | null },
	targetStatus: TicketStatus,
	canManage: boolean,
	viewerId: string | undefined,
): boolean {
	if (ticket.status === targetStatus) return false
	const canSetStatus =
		canManage || (viewerId != null && ticket.assignee?.id === viewerId)
	if (!canSetStatus) return false
	return getAllowedTicketStatusTargets(ticket.status, canManage).includes(
		targetStatus,
	)
}

export function canCreateTicketInBoardStatus(
	status: TicketStatus,
	canManage: boolean,
): boolean {
	return canManage && status !== "done"
}

export function inlineCreateTicketButtonLabel(
	createLabel: string,
	statusLabel: string,
): string {
	return `${createLabel} to ${statusLabel}`
}
